import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Province, Region, SmsAdv, SmsAdvCriteriaFields, SmsAdvFields, SmsAdvLabel, ZipCodes } from '@models/smsadv/smsadv';
import { TranslateService } from '@ngx-translate/core';
import { SmsAdvCreateTabHandlerService, SmsAdvDirectionTab } from '@services/sms-adv-create-tab-handler.service';
import { formateDateTimeWithSeconds } from '@utils/utils';
import { Subscription } from 'rxjs';
import { TabSmsAdv } from '../sms-adv-create.component';

@Component({
  selector: 'wsb-sms-adv-summary-campaign',
  templateUrl: './sms-adv-summary-campaign.component.html',
  styleUrls: ['./sms-adv-summary-campaign.component.scss'],
  standalone: false
})
export class SmsAdvSummaryCampaignComponent implements OnInit, OnDestroy {
  @Input() smsAdvCampaignToSend: SmsAdv;
  @Output() isEnableSendBtn: EventEmitter<boolean> = new EventEmitter<boolean>();

  readonly STRING_EMPTY = '';
  readonly STRING_SPACE = ' ';

  tabSubscription: Subscription;

  constructor(private translateService: TranslateService, private tabHandlerService: SmsAdvCreateTabHandlerService) {}
  ngOnDestroy(): void {
    this.tabSubscription.unsubscribe();
  }

  ngOnInit(): void {
    this.handlerTabClose();
  }

  getGender() {
    const genders: string[] = this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELD_GENDERS);
    if (genders === undefined) {
      return this.STRING_EMPTY;
    }
    if (genders.length > 1) {
      const male = this.getTranslateLabels(SmsAdvLabel.SUMMARY_GENDERS + genders[0].toLowerCase());
      const female = this.getTranslateLabels(SmsAdvLabel.SUMMARY_GENDERS + genders[1].toLowerCase());
      return male + ' , ' + female;
    }
    return this.getTranslateLabels(SmsAdvLabel.SUMMARY_GENDERS + genders[0].toLowerCase());
  }

  getAgeRanges() {
    const ageRanges: string[] = this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELD_AGE_RANGES);
    if (ageRanges !== undefined && ageRanges.length > 0) {
      return ageRanges;
    }
    return this.STRING_EMPTY;
  }

  get isRegion() {
    const regions: Region[] = this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELD_REGIONS);
    if (regions !== undefined && regions.length > 0) {
      return true;
    }
    return false;
  }

  get regionValue() {
    const regions: Region[] = this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELD_REGIONS);
    const result = [];
    regions.forEach(r => result.push(this.getRegionRender(r)));
    return result.join();
  }

  get isProvinces() {
    const provinces: Province[] = this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELDS_PROVINCES);
    if (provinces !== undefined && provinces.length > 0) {
      return true;
    }
    return false;
  }

  get provincesValue() {
    const provinces: Province[] = this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELDS_PROVINCES);
    const result = [];
    provinces.forEach(pr => result.push(this.getProvincesRender(pr)));
    return result.join();
  }

  get isZipCodes() {
    const zipCodes: ZipCodes[] = this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELD_ZIP_CODES);
    if (zipCodes !== undefined && zipCodes.length > 0) {
      return true;
    }
    return false;
  }

  get zipCodesValue() {
    const zipCodes: ZipCodes[] = this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELD_ZIP_CODES);
    const result = [];
    zipCodes.forEach(zip => result.push(this.getZipCodesRender(zip)));
    return result.join();
  }

  getSending() {
    return this.sendingTypeValue + this.scheduledDateValue;
  }

  getRecipientCampaign() {
    if (this.smsAdvCampaignToSend) {
      if (this.smsAdvCampaignToSend.limitRecipients > 0) {
        return this.smsAdvCampaignToSend.limitRecipients;
      }
      return this.smsAdvCampaignToSend.countRecipients;
    }
    return 0;
  }

  get sendingTypeValue() {
    const sendingType: string = this.getSmsAdvField(SmsAdvFields.FIELD_SENDING_TYPE);
    if (sendingType) {
      const labels = SmsAdvLabel.SUMMARY_SEND_TYPE + sendingType.toLowerCase();
      return this.getTranslateLabels(labels);
    }
    return this.STRING_EMPTY;
  }

  routeToSelectRecipient() {
    this.tabHandlerService.setChangeTab(SmsAdvDirectionTab.JUMP_SELECT_RECIPIENT);
  }

  routeToCreateCampaign() {
    this.tabHandlerService.setChangeTab(SmsAdvDirectionTab.JUMP_CREATE_CAMPAIGN);
  }

  // private method
  private handlerTabClose() {
    this.tabSubscription = this.tabHandlerService.closeTab$.subscribe(tab => {
      if (tab === TabSmsAdv.TAB_SUMMARY_CAMPAIGN) {
        return this.tabHandlerService.setTabIsCompleted(TabSmsAdv.TAB_SUMMARY_CAMPAIGN);
      }
    });
  }

  private get scheduledDateValue() {
    const scheduledDate = this.getSmsAdvField(SmsAdvFields.FIELD_SCHEDULED_DATE);
    if (scheduledDate) {
      return ' (' + this.getFormatScheduledDate(scheduledDate) + ')';
    }
    return this.STRING_EMPTY;
  }

  private getFormatScheduledDate(scheduledDate) {
    const dateTime = formateDateTimeWithSeconds(scheduledDate).split(' ');
    const date = dateTime[0];
    const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
    return date + this.STRING_SPACE + time;
  }

  private get recipientCriteria() {
    return this.getSmsAdvField(SmsAdvFields.FIELD_CRITERIA);
  }

  private getRegionRender(region: Region) {
    return this.STRING_SPACE + region.name;
  }

  private getProvincesRender(province: Province) {
    return this.STRING_SPACE + province.abbreviation;
  }

  private getZipCodesRender(zipCode: ZipCodes) {
    return this.STRING_SPACE + zipCode.zipCode;
  }

  private getRecipientCriteriaValue(field: string) {
    if (this.recipientCriteria && this.recipientCriteria[field]) {
      return this.recipientCriteria[field];
    }
    return undefined;
  }

  private getSmsAdvField(field: string) {
    if (this.smsAdvCampaignToSend) {
      return this.smsAdvCampaignToSend[field];
    }
    return undefined;
  }

  private getTranslateLabels(value: string) {
    if (value) {
      let labels;
      this.translateService.get(value).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }
}
